import { nestPieces, ROTATION_SETS } from "./nesting";
import { arrangeStripSets } from "./pairs";
import { area, signedArea, bbox, worldPoints, autoRoundTips, glassMatch, mirrorPiece, offsetPolygon, rotatePt, loops, pointInPoly } from "./geometry";

const NEST_PAD = 1 / 16;

export const ROUND_TYPES = ["front_door", "rear_door", "quarter", "front_vent", "windshield", "windshield_strip", "back_glass", "sunroof", "other", "nav_screen"]; // nav screens round too (PPF never)
export const ROUND_STRENGTH = { off: 0, x64: 0.015625, hair: 0.03125, tiny: 0.0625, xsmall: 0.125, small: 0.25, medium: 0.5, large: 0.75 }; // fillet radius, inches (a needle tip is cut back ≤ 0.35")
export const ROUND_OPTIONS = [["off", "Off"], ["x64", "1/64″"], ["tiny", "1/16″"], ["xsmall", "1/8″"], ["small", "1/4″"], ["medium", "1/2″"], ["large", "3/4″"]];
export const ROUND_LABEL = Object.fromEntries(ROUND_OPTIONS);
// Frameless glass (Tesla, Porsche, convertibles…): the top corners show on the car, so only a small "pointy round" is offered — the real glass corner is ~3/8"
export const FRAMELESS_ROUND_OPTIONS = [["off", "Off"], ["x64", "1/64″"], ["hair", "1/32″"], ["tiny", "1/16″"], ["xsmall", "1/8″"]];  // just the tip: 1/64" only knocks the point off
export const FRAMELESS_ROUND_DEFAULT = "x64";
// Glass-match (frameless door glass): the glass corner radius and the hairline margin, saved per vehicle by the owner
export const GLASS_RADIUS_OPTIONS = [[0.125, "1/8″"], [0.1875, "3/16″"], [0.25, "1/4″"], [0.375, "3/8″"], [0.5, "1/2″"]];
// Exposed top edge of frameless glass: DigiCut's outline can sit a hair inside the glass there — push it out to the edge, per vehicle
export const GLASS_TOP_OPTIONS = [[0, "0"], [1 / 32, "1/32″"], [1 / 16, "1/16″"], [3 / 32, "3/32″"], [1 / 8, "1/8″"], [3 / 16, "3/16″"]];
export const GLASS_MARGIN_OPTIONS = [[0, "0"], [1 / 32, "1/32″"], [1 / 16, "1/16″"]];
export const GLASS_TYPES = ["front_door", "rear_door"];
export const GLASS_MARGIN = 1 / 32;  // fixed hairline inside the glass edge; the Edge dropdown is the installer's dial on top of it
export const glassOf = (v) => ({ r: v?.glass_radius ?? 0.25, m: GLASS_MARGIN, t: v?.glass_top ?? 0 });
// Glass-match for one frameless door pattern: top edge out to the glass (per vehicle), the glass's corner radius, then the hairline margin
export const glassFit = (pts, g) => glassMatch(g.t ? extendTop(pts, g.t) : pts, g.r, g.m);
// Gasket: "to" = cut to the rubber seal, "below" = roll-up patterns 1/16" smaller all around so the film tucks under the seal (bottom-loading)
export const GASKET_TYPES = ["front_door", "rear_door"];
export const GASKET_INSET = 1 / 16;
// Frameless door glass (no top channel — the top edge of the film IS the top edge of the glass): no top allowance, cut flush.
const FRAMELESS = [
  [/^tesla$/i, /./],
  [/^(subaru|toyota|scion)$/i, /\b(brz|gr ?86|fr-s|86)\b/i],
  [/^ford$/i, /\bmustang\b/i], [/^chevrolet$/i, /\b(camaro|corvette)\b/i], [/^dodge$/i, /\bchallenger\b/i],
  [/^porsche$/i, /./], [/^jaguar$/i, /\bf-?type\b/i], [/^nissan$/i, /\b(3[57]0z|z|gt-?r)\b/i], [/^mazda$/i, /\b(mx-?5|miata)\b/i],
  [/^audi$/i, /\b(a5|s5|rs ?5|a7|s7|rs ?7|tt|r8|e-tron gt)\b/i], [/^bmw$/i, /\b([2468] ?series|i4|i8|z4|m[2468])\b/i],
  [/^mercedes/i, /\b(coupe|cabrio|convertible|cls|cla|amg gt|sl)\b/i], [/^lucid$/i, /./],
];
// every convertible / roadster has frameless glass; Subaru Forester ≤ 2008, Impreza ≤ 2007, Legacy / Outback ≤ 2009 did too
export const isFrameless = (v) => Boolean(v && (/convertible|cabrio|roadster|spyder|spider/i.test(v.body_style || "")
  || FRAMELESS.some(([mk, md]) => mk.test(v.make || "") && md.test(v.model || ""))
  || (/^subaru$/i.test(v.make || "") && ((/\bforester\b/i.test(v.model || "") && (v.year_start || 9999) <= 2008) || (/\bimpreza\b/i.test(v.model || "") && (v.year_start || 9999) <= 2007) || (/\b(legacy|outback)\b/i.test(v.model || "") && (v.year_start || 9999) <= 2009)))));
// Top edge allowance: roll-up windows get this much extra film along the top so it reaches up into the top channel when the
// window is rolled up (DigiCut-style). Owner setting, remembered on this computer; 0 = cut exactly to the glass outline.
export const TOP_EXTEND_OPTIONS = [0, 1 / 8, 1 / 4, 3 / 8, 1 / 2];
// Top allowance defaults to 0 on every roll-up (templates already carry the glass edge); an installer can still pick +1/8…+1/2".
let topExtend = Number(localStorage.getItem("xpattern.topExtend.v2") ?? 0) || 0;
export const getTopExtend = () => topExtend;
export const setTopExtendValue = (v) => { topExtend = v; localStorage.setItem("xpattern.topExtend.v2", String(v)); };
// Edge offset: grows (+) or shrinks (−) every window outline all around — dials a flush cut to the installer's plotter / film.
export const EDGE_OFFSET_OPTIONS = [-1 / 16, -1 / 32, 0, 1 / 32, 1 / 16];
let edgeOffset = Number(localStorage.getItem("xpattern.edgeOffset") ?? 0) || 0;
export const getEdgeOffset = () => edgeOffset;
export const setEdgeOffsetValue = (v) => { edgeOffset = v; localStorage.setItem("xpattern.edgeOffset", String(v)); };
export const edgeOffsetLabel = (v) => (v === 0 ? "0" : `${v > 0 ? "+" : "−"}1/${Math.round(1 / Math.abs(v))}″`);
const WINDOW_TYPE = (t) => !/^(ppf_|nav|decal)/.test(t || "");
export const extendTop = (pts, d) => {
  // Every edge whose outward normal points up is moved up by d (full weight when it faces straight up, none on the sides / bottom);
  // each vertex is then the meeting point of its two moved edges, so a top corner stays as sharp as DigiCut draws it — the side edge
  // simply runs on up to the raised top edge instead of the corner being dragged into a hook.
  const n = pts.length;
  if (n < 3 || !d) return pts;
  const ccw = signedArea(pts) > 0;
  const lift = (a, b) => {
    let nx = b[1] - a[1], ny = -(b[0] - a[0]);
    if (!ccw) { nx = -nx; ny = -ny; }
    const up = -ny / (Math.hypot(nx, ny) || 1); // 1 = edge faces straight up (screen y grows downward)
    return d * Math.min(1, Math.max(0, (up - 0.2) / 0.6));
  };
  const segX = (a, b, c, e) => {
    const den = (b[0] - a[0]) * (e[1] - c[1]) - (b[1] - a[1]) * (e[0] - c[0]);
    if (Math.abs(den) < 1e-12) return null;
    const t = ((c[0] - a[0]) * (e[1] - c[1]) - (c[1] - a[1]) * (e[0] - c[0])) / den, u = ((c[0] - a[0]) * (b[1] - a[1]) - (c[1] - a[1]) * (b[0] - a[0])) / den;
    return t > 1e-9 && t < 1 - 1e-9 && u > 1e-9 && u < 1 - 1e-9 ? [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])] : null;
  };
  // tiny local loops where a few close-set vertices (a chamfered kink) crossed over after the lift: collapse them to the crossing
  const dropLoops = (poly) => {
    let cur = poly;
    for (let guard = 0; guard < 30; guard++) {
      const m = cur.length;
      let hit = null;
      for (let i = 0; i < m && !hit; i++) for (let k = 2; k <= 8 && k < m - 1; k++) { const j = (i + k) % m; const x = segX(cur[i], cur[(i + 1) % m], cur[j], cur[(j + 1) % m]); if (x) { hit = [i, j, x]; break; } }
      if (!hit) return cur;
      const [i, j, x] = hit, span = (j - i + m) % m, out = [];
      for (let q = 0; q < m; q++) { const rel = (q - i + m) % m; if (rel >= 1 && rel <= span) continue; out.push(cur[q]); if (q === i) out.push(x); }
      cur = out;
    }
    return cur;
  };
  return dropLoops(pts.map((p, i) => {
    const a = pts[(i - 1 + n) % n], b = pts[(i + 1) % n];
    const s1 = lift(a, p), s2 = lift(p, b);
    if (!s1 && !s2) return p;
    const d1 = [p[0] - a[0], p[1] - a[1]], d2 = [b[0] - p[0], b[1] - p[1]];
    const cross = d1[0] * d2[1] - d1[1] * d2[0];
    const turn = Math.abs(Math.atan2(cross, d1[0] * d2[0] + d1[1] * d2[1])) * 180 / Math.PI;
    if (turn < 20 || Math.abs(s1 - s2) < 0.5 * d) return [p[0], p[1] - lift(a, b)]; // smooth run: lift by the vertex normal
    const P1 = [a[0], a[1] - s1], P2 = [p[0], p[1] - s2];
    const t = ((P2[0] - P1[0]) * d2[1] - (P2[1] - P1[1]) * d2[0]) / cross;
    const q = [P1[0] + t * d1[0], P1[1] + t * d1[1]];
    return Math.hypot(q[0] - p[0], q[1] - p[1]) > 2.5 * d ? [p[0], p[1] - Math.max(s1, s2)] : q; // razor-sharp corner: plain lift
  }));
};
export const roundedPoints = (pt, strength, gasket = "to", framelessStrength = FRAMELESS_ROUND_DEFAULT) => {
  const type = pt.piece_type ?? pt.pieceType;
  // corners come out as drawn plus the shop's radius (account setting). Frameless door glass (Tesla, Porsche, Mustang, convertibles…)
  // is glass-matched instead: DigiCut draws its exposed corners sharp while the glass is round, so the film carries the glass radius
  // and a hairline margin (per vehicle). No gasket inset or top extend there — there is no channel on frameless glass; edge offset still applies
  if (pt.frameless && GLASS_TYPES.includes(type)) {
    const g = pt.glass || glassOf(null);
    let gp = glassFit(pt.rawPoints || pt.points, g);
    if (edgeOffset) gp = offsetPolygon(gp, edgeOffset);
    return gp;
  }
  const r = ROUND_STRENGTH[pt.frameless ? framelessStrength : strength] || 0;
  let pts = r && ROUND_TYPES.includes(type) ? autoRoundTips(pt.rawPoints || pt.points, r) : pt.rawPoints || pt.points;
  if (gasket === "below" && GASKET_TYPES.includes(type)) pts = offsetPolygon(pts, -GASKET_INSET);
  if (edgeOffset && WINDOW_TYPE(type)) pts = offsetPolygon(pts, edgeOffset);
  if (topExtend && GASKET_TYPES.includes(type) && !pt.frameless) pts = extendTop(pts, topExtend); // frameless glass has no top channel: cut flush
  return pts;
};

// Vehicles whose 3-piece slider back glass DigiCut nests tipped & stacked (owner: Mazda B-Series only, incl. future uploads).
export const stackedSlider = (v) => /^mazda$/i.test(v?.make || "") && /\bb[- ]?series\b/i.test(v?.model || "");

export function buildPieces(patterns, autoRound = "tiny", vehicle = null, gasket = "to", framelessRound = FRAMELESS_ROUND_DEFAULT) {
  const sliderStack = stackedSlider(vehicle), frameless = isFrameless(vehicle);
  return arrangeStripSets(patterns.map((pt) => {
    const glass = frameless ? glassOf(vehicle) : undefined;
    const points = roundedPoints({ ...pt, frameless, glass }, autoRound, gasket, framelessRound);
    const panes = (pt.holes || []).some((h) => h.length && !pointInPoly(h[0], pt.points)); // a CORE pattern of several panes: extra loops outside the outline
    const mb = pt.measure || bbox(panes ? loops(pt) : pt.raw_points || pt.points); // exact uploaded measurements — what the list shows
    return {
      measure: { w: mb.w, h: mb.h }, frameless, glass, multi: panes,
      id: pt.id, patternId: pt.id, name: pt.name, pieceType: pt.piece_type, side: pt.side, version: pt.version, ownerId: pt.owner_id, setKey: pt.set_key || "", setName: pt.set_name || "", setOffset: pt.set_offset || null, sliderStack,
      approximate: Boolean(pt.approximate), publisherName: pt.publisher_name, publisherTier: pt.publisher_tier, status: pt.status,
      rawPoints: pt.points, points, basePoints: points, holes: pt.holes || [], x: 0, y: 0, rotation: 0, included: false, appliedOffset: 0, dirty: false, cleaned: true, autoFixed: true, // library outlines arrive already cleaned by the server — no second pass on select
    };
  }));
}

// Decal flip: mirrored outline for the opposite side of the vehicle, in place, job-only (never written back to the library)
export function flipDecal(p) {
  const np = mirrorPiece(p);
  return { points: np.points, basePoints: np.points, holes: np.holes, x: np.x, y: np.y, edited: true, mirrored: !p.mirrored, dirty: false };
}

// Sticker runs: `qty` identical copies of a decal laid out as a clean grid below everything else on the film — across the
// film first, turned 90° when that fits more per row. Copies carry `copyOf` so they follow the original (qty change / removal).
export function decalGrid(pieces, base, qty, state) {
  const { rollWidth, spacing, margin } = opts(state), usable = rollWidth - 2 * margin;
  const others = pieces.filter((p) => p.id !== base.id && p.copyOf !== base.id);
  const pick = [0, 90].map((rot) => { const rb = bbox(loops(base).map((pt) => rotatePt(pt, rot))); return { rot, rb, cols: Math.floor((usable + spacing) / (rb.w + spacing)) }; })
    .filter((o) => o.cols > 0).sort((a, b) => b.cols - a.cols || a.rb.h - b.rb.h)[0];
  if (!pick) return null;
  const used = others.filter((p) => p.included).map((p) => bbox(worldPoints(p)).maxY);
  const startY = used.length ? Math.max(...used) + spacing : margin;
  const run = Array.from({ length: qty }, (_, i) => {
    const c = i === 0 ? base : { ...base, id: `${base.id}-c${i + 1}`, copyOf: base.id, pv: undefined, pvRot: undefined, pvSide: undefined };
    const col = i % pick.cols, row = Math.floor(i / pick.cols);
    return { ...c, included: true, rotation: pick.rot, x: margin + col * (pick.rb.w + spacing) - pick.rb.minX, y: startY + row * (pick.rb.h + spacing) - pick.rb.minY };
  });
  return { pieces: [...others, ...run], cols: pick.cols, rows: Math.ceil(qty / pick.cols) };
}

// Decal sizing: one axis typed, the whole outline (and its cut-outs) scales by the same factor from its top-left corner.
export function scalePiece(p, axis, n) {
  const bb = bbox(loops(p)), cur = axis === "L" ? bb.w : bb.h, k = n / cur;
  if (!(k > 0) || Math.abs(k - 1) < 1e-4) return null;
  const sc = ([x, y]) => [bb.minX + (x - bb.minX) * k, bb.minY + (y - bb.minY) * k];
  return { points: p.points.map(sc), basePoints: p.points.map(sc), holes: (p.holes || []).map((h) => h.map(sc)), edited: true, appliedOffset: 0, dirty: !p.decalId };
}

// Centre every row of placed pieces across the film so nothing sits off to one side of the grid.
export function centerRows(pieces, rollWidth, margin = 0) {
  const inc = pieces.filter((p) => p.included).map((p) => ({ p, bb: bbox(worldPoints(p)) }));
  if (!inc.length) return pieces;
  inc.sort((a, b) => a.bb.minY - b.bb.minY);
  const rows = [];
  for (const it of inc) {
    const row = rows.find((r) => it.bb.minY < r.maxY - 1e-6 && it.bb.maxY > r.minY + 1e-6);
    if (row) { row.items.push(it); row.minY = Math.min(row.minY, it.bb.minY); row.maxY = Math.max(row.maxY, it.bb.maxY); }
    else rows.push({ items: [it], minY: it.bb.minY, maxY: it.bb.maxY });
  }
  const shift = {};
  for (const r of rows) {
    const minX = Math.min(...r.items.map((i) => i.bb.minX)), maxX = Math.max(...r.items.map((i) => i.bb.maxX));
    const dx = (rollWidth - (maxX - minX)) / 2 - minX;
    if (minX + dx < margin - 1e-6 || maxX + dx > rollWidth - margin + 1e-6) continue;
    r.items.forEach((i) => { shift[i.p.id] = dx; });
  }
  return pieces.map((p) => (shift[p.id] ? { ...p, x: p.x + shift[p.id] } : p));
}

// Shadow Cut on the roll: neighbours need room for two weeding contours between them and the roll edge for one
const shadowPad = (state) => (state.shadowMode && state.pieces.some((p) => p.included && p.shadow && !p.multi) ? state.shadowGap || 0 : 0);
const opts = (state) => ({ rollWidth: state.rollWidth, spacing: state.spacing + NEST_PAD + 2 * shadowPad(state), margin: state.margin + shadowPad(state), rotations: ROTATION_SETS[state.rotationSet].angles });

export function applyNest(pieces, state) {
  const inc = pieces.filter((p) => p.included);
  const res = nestPieces(inc, opts(state));
  const map = Object.fromEntries(res.placements.map((p) => [p.id, p]));
  return { pieces: pieces.map((p) => (map[p.id] ? { ...p, x: map[p.id].x, y: map[p.id].y, rotation: map[p.id].rotation } : p)), result: res };
}

export const nestOpts = opts;

// Rotation that shows a pattern on the roll exactly as its sidebar preview (the canvas draws the roll turned 90°).
export const PREVIEW_ROT = 90;

export function addToRoll(pieces, ids, state, rotations, strictRotations = false) {
  const fixed = pieces.filter((p) => p.included && !ids.includes(p.id)).map((p) => ({ id: p.id, x: p.x, y: p.y, rotation: p.rotation, world: worldPoints(p) }));
  const fresh = pieces.filter((p) => ids.includes(p.id));
  const o = opts(state);
  const res = nestPieces(fresh, rotations ? { ...o, rotations, strictRotations } : o, fixed);
  const map = Object.fromEntries(res.placements.map((p) => [p.id, p]));
  return {
    pieces: pieces.map((p) => (map[p.id] ? { ...p, included: true, x: map[p.id].x, y: map[p.id].y, rotation: map[p.id].rotation } : p)),
    overflow: res.overflow,
  };
}

export function usage(pieces, worlds, state) {
  const inc = pieces.filter((p) => p.included);
  if (!inc.length) return { count: 0, length: 0, utilization: 0, area: 0 };
  const maxY = inc.reduce((m, p) => Math.max(m, bbox(worlds[p.id]).maxY), 0);
  const length = maxY + state.margin;
  const a = inc.reduce((s, p) => s + area(p.points), 0);
  return { count: inc.length, length, area: a, utilization: a / (length * state.rollWidth) };
}

export const selectMachinePatch = (m) => ({ machine: m, machineSettings: { speed: m.default_speed, force: m.default_force, blade_offset_mm: m.blade_offset_mm, passes: 1, return_home: true, feed_out: true, offset_compensation: false, curve_slowdown: false, relative: Boolean(m.relative) } });

const capSide = (s) => s[0].toUpperCase() + s.slice(1);
// "Left Front Rollup" → "Front Door, Left"; centre pieces keep their name ("Back Glass V2").
export const displayName = (p) => {
  if (p.eyebrow) return `Windshield Eyebrow (custom) · ${p.eyebrow.depth}"${p.eyebrowHalf ? ` · ${p.eyebrowHalf === "left" ? "Left" : "Right"} half` : ""}`;
  if (p.trimGroup) return `${p.name} · piece ${p.trimNo || 1}`;
  if (/,\s*(Left|Right)\b/.test(p.name)) return p.name; // already in house style ("Front Quarter, Left")
  const base = p.name.replace(/^(left|right|lh|rh)\s+/i, "").replace(/[\s,]+(left|right|lh|rh)$/i, "").replace(/\bRoll-?ups?\b/gi, "Door");
  return p.side === "left" || p.side === "right" ? `${base}, ${capSide(p.side)}` : base;
};


// House order for window-tint lists (sidebar templates, upload review): Back Glass → Front Door → Rear Door → Rear Quarter
// → Front Quarter → anything else (Sunroof…) → Eyebrow → Windshield. Kits, panels and graphics all rank 0 (plain A → Z).
export const windowRank = (pieceType, name) => {
  const t = pieceType || "", n = (name || "").toLowerCase();
  if (/^ppf_/.test(t) || ["decal", "nav_screen", "gauge_cluster"].includes(t)) return 0;
  if (t === "back_glass" || /back glass/.test(n)) return 0;
  if (t === "front_vent" || /front quarter|front door fixed|rear door fixed/.test(n)) return 5;
  if (t === "front_door" || /front door|front roll/.test(n)) return 1;
  if (t === "rear_door" || /rear door|rear roll/.test(n)) return 2;
  if (t === "quarter" || /quarter/.test(n)) return 3;
  if (t === "windshield_strip" || /eyebrow|visor|strip/.test(n)) return 8;
  if (t === "windshield" || /windshield/.test(n)) return 9;
  return 6;
};
export const sideRank = (side) => (side === "left" ? 0 : side === "right" ? 1 : 2);
